import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useVideoCall } from "contexts/VideoCallContext.jsx";
import dayjs from "dayjs";

import { JoinVideoButton } from "components/JoinVideoButton/JoinVideoButton.jsx";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { Select } from "components/ui/Select/Select";
import { VideoShareActions } from "components/VideoShareActions/VideoShareActions.jsx";
import { useAppointment } from "features/appointments/hooks/useAppointment";
import { useUpdateConsultationType } from "features/appointments/hooks/useUpdateConsultationType";
import { formatBookedAt } from "features/appointments/utils/formatBookedAt";
import { VideoCallSlot } from "features/consultations/components/VideoCallSlot";
import { useAddConsultationService } from "features/consultations/hooks/useAddConsultationService";
import { useConsultationNote } from "features/consultations/hooks/useConsultationNote";
import { useFinishConsultation } from "features/consultations/hooks/useFinishConsultation";
import { useConsultOutcomes } from "features/practice/hooks/useConsultOutcomes";
import { usePracticeServices } from "features/practice/hooks/usePracticeServices";

import { StartConsultationForm } from "../StartConsultationForm/StartConsultationForm";
import { PaymentSection } from "./components/PaymentSection.jsx";
import style from "./ConsultationBox.module.css";

const formatConsultationType = (type) => {
  if (!type) return "";

  return type
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const formatKey = (key) =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const parseAdditionalData = (additionalData) => {
  if (!additionalData) return null;

  try {
    const data =
      typeof additionalData === "string"
        ? JSON.parse(additionalData)
        : additionalData;

    if (!data || typeof data !== "object" || Object.keys(data).length === 0) {
      return null;
    }

    return data;
  } catch (error) {
    console.error("Error parsing additional data:", error);
    return null;
  }
};

export const ConsultationBox = ({
  patient,
  staffName,
  consultation,
  onFinish,
}) => {
  const isPaid = consultation?.moneyOwed === false;
  const hasAppointment = patient && patient?.nextApppointment;
  const [service, setService] = useState("Select Service");
  const [isModalOpen, setIsModalOpen] = useState({ open: false, id: null });
  const [showVideoConfirmModal, setShowVideoConfirmModal] = useState(false);
  const [consultationOutcome, setConsultationOutcome] = useState("");
  const [convertedToVideo, setConvertedToVideo] = useState(false);
  const [convertedAppointment, setConvertedAppointment] = useState(null);
  const hasActiveConsultation = !!consultation;

  const { videoCredentials, showInlineVideo, handleJoinVideo } = useVideoCall();

  const isVideoConsultation =
    convertedToVideo || consultation?.appointment?.consultationType === "VIDEO";

  const { data: nextAppointment } = useAppointment(patient?.nextAppointmentId);
  const bookedAt = formatBookedAt(nextAppointment?.created);
  const hasNextAppointment = !!patient?.nextAppointmentId;
  const showAppointmentTab = hasNextAppointment;
  const shouldDefaultToAppointmentTab = hasNextAppointment;

  const additionalData = parseAdditionalData(
    consultation?.appointment?.additionalData ?? nextAppointment?.additionalData
  );
  const showDetailsTab = !!additionalData;

  const [activeTab, setActiveTab] = useState(
    shouldDefaultToAppointmentTab
      ? "appointment"
      : isVideoConsultation
      ? "video"
      : "services"
  );

  const hasInitializedDefaultTabRef = useRef(false);

  useEffect(() => {
    if (hasInitializedDefaultTabRef.current) return;
    if (!patient) return;
    hasInitializedDefaultTabRef.current = true;
    if (shouldDefaultToAppointmentTab) {
      setActiveTab("appointment");
    } else if (isVideoConsultation) {
      setActiveTab("video");
    }
  }, [patient, shouldDefaultToAppointmentTab, isVideoConsultation]);

  useEffect(() => {
    if (videoCredentials && showInlineVideo) {
      setActiveTab("video");
    }
  }, [videoCredentials, showInlineVideo]);

  useEffect(() => {
    if (isVideoConsultation && activeTab === "services") {
      setActiveTab("video");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVideoConsultation]);

  const { data: consultationNote } = useConsultationNote(consultation?.id);

  const { data: services, isLoading: isServicesLoading } =
    usePracticeServices();

  useEffect(() => {
    if (consultation?.mainPracticeServiceId && services?.length) {
      const match = services.find(
        (s) => s.id === consultation.mainPracticeServiceId
      );
      if (match) setService(String(match.id));
    }
  }, [consultation?.mainPracticeServiceId, services]);

  const { data: consultationOutcomes, isLoading: isOutcomesLoading } =
    useConsultOutcomes();

  const hasOutcomes = consultationOutcomes && consultationOutcomes.length > 0;
  if (!isOutcomesLoading && !hasOutcomes) {
    console.warn(
      "[ConsultationBox] No consultation outcomes configured. Set them in Practice Settings."
    );
  }

  const finishConsultation = useFinishConsultation(
    consultation?.id,
    patient?.id,
    consultation
  );
  const addConsultationService = useAddConsultationService(consultation?.id);
  const updateConsultationType = useUpdateConsultationType();

  const handleCreateVideo = () => {
    if (!consultation?.appointment?.id) {
      toast.error(
        "This consultation has no linked appointment. Please start the consultation with the 'Correlate to the scheduled appointment' option enabled, or schedule an appointment first."
      );
      setShowVideoConfirmModal(false);
      return;
    }
    updateConsultationType.mutate(
      { appointmentId: consultation.appointment.id },
      {
        onSuccess: (data) => {
          toast.success("Consultation updated to video call");
          setConvertedToVideo(true);
          setConvertedAppointment(data);
          setActiveTab("video");
          setShowVideoConfirmModal(false);
        },
        onError: () => {
          toast.error("Failed to update consultation type");
        },
      }
    );
  };

  const handleFinishConsultation = () => {
    if (!consultationNote || consultationNote?.length === 0) {
      toast.error("Please add consultation note before finishing");
      return;
    }

    if (hasOutcomes && !consultationOutcome) {
      toast.error("Please select a consultation outcome");
      return;
    }

    onFinish();
    finishConsultation.mutate(consultationOutcome || null);
  };

  return (
    <>
      <div className={style.consultationBoxWrapper}>
        <section className={style.consultationBox}>
          <div className={style.consultationBoxTop}>
            <div className={style.consultationBoxTopItem}>
              <p>
                {hasActiveConsultation
                  ? "Current Consultation"
                  : "Planned consultation"}
              </p>
              <p>
                {hasActiveConsultation
                  ? dayjs(consultation?.created, "DD-MM-YYYY HH:mm").format(
                      "DD MMM YYYY HH:mm"
                    )
                  : hasAppointment
                  ? patient.nextApppointment
                  : "None scheduled"}
              </p>
              {!!consultation?.appointment?.companyName && (
                <p>Company: {consultation.appointment.companyName}</p>
              )}
            </div>
            <div
              style={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              {hasActiveConsultation && hasOutcomes && (
                <Select
                  name="consultationOutcome"
                  options={
                    isOutcomesLoading
                      ? []
                      : (consultationOutcomes ?? []).map((outcome) => ({
                          label: outcome,
                          value: outcome,
                        }))
                  }
                  value={consultationOutcome}
                  onChange={(value) => setConsultationOutcome(value)}
                  placeholder="Select Outcome *"
                  fullWidth
                />
              )}
              {hasActiveConsultation ? (
                <>
                  {!isVideoConsultation && (
                    <button
                      type="button"
                      onClick={() => setShowVideoConfirmModal(true)}
                      className={style.videoIconButton}
                      title="Convert to video consultation"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M23 7l-7 5 7 5V7z" />
                        <rect
                          x="1"
                          y="5"
                          width="15"
                          height="14"
                          rx="2"
                          ry="2"
                        />
                      </svg>
                    </button>
                  )}
                  <Button
                    variant="primary"
                    onClick={() => handleFinishConsultation()}
                    className={style.button}
                  >
                    Finish consultation
                  </Button>
                </>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => setIsModalOpen({ open: true })}
                  className={style.button}
                >
                  Start consultation
                </Button>
              )}
            </div>
          </div>

          <div className={style.tabsContainer}>
            <div className={style.tabsWrapper}>
              {showAppointmentTab && (
                <button
                  type="button"
                  className={`${style.tab} ${
                    activeTab === "appointment" ? style.tabActive : ""
                  }`}
                  onClick={() => setActiveTab("appointment")}
                >
                  Appointment
                </button>
              )}
              {showDetailsTab && (
                <button
                  type="button"
                  className={`${style.tab} ${
                    activeTab === "details" ? style.tabActive : ""
                  }`}
                  onClick={() => setActiveTab("details")}
                >
                  Details
                </button>
              )}
              {isVideoConsultation && (
                <button
                  type="button"
                  className={`${style.tab} ${
                    activeTab === "video" ? style.tabActive : ""
                  }`}
                  onClick={() => setActiveTab("video")}
                >
                  Video
                </button>
              )}
              <button
                type="button"
                className={`${style.tab} ${
                  activeTab === "services" ? style.tabActive : ""
                }`}
                onClick={() => setActiveTab("services")}
              >
                Payment
              </button>
            </div>
          </div>

          <div className={style.tabContent}>
            {activeTab === "appointment" && showAppointmentTab && (
              <div className={style.appointmentDetails}>
                {nextAppointment ? (
                  <>
                    {!!nextAppointment.appointmentStatus && (
                      <div className={style.appointmentStatusRow}>
                        <span
                          className={`${style.appointmentStatusBadge} ${
                            nextAppointment.appointmentStatus === "CONFIRMED"
                              ? style.appointmentStatusConfirmed
                              : nextAppointment.appointmentStatus === "CANCELLED"
                              ? style.appointmentStatusCancelled
                              : nextAppointment.appointmentStatus === "COMPLETED"
                              ? style.appointmentStatusCompleted
                              : nextAppointment.appointmentStatus === "NO_SHOW"
                              ? style.appointmentStatusNoShow
                              : style.appointmentStatusDefault
                          }`}
                        >
                          {formatConsultationType(nextAppointment.appointmentStatus)}
                        </span>
                      </div>
                    )}
                    <div className={style.appointmentHeader}>
                      <div className={style.appointmentHeaderMain}>
                        <span className={style.appointmentHeaderLabel}>
                          Scheduled appointment
                        </span>
                        <h3 className={style.appointmentHeaderDate}>
                          {nextAppointment.startTime
                            ? dayjs(
                                nextAppointment.startTime,
                                "YYYY-MM-DD HH:mm"
                              ).format("ddd, DD MMM YYYY")
                            : "—"}
                        </h3>
                        <p className={style.appointmentHeaderTime}>
                          {nextAppointment.startTime
                            ? dayjs(
                                nextAppointment.startTime,
                                "YYYY-MM-DD HH:mm"
                              ).format("HH:mm")
                            : "—"}
                          {!!nextAppointment.durationInMins && (
                            <span className={style.appointmentHeaderDuration}>
                              · {nextAppointment.durationInMins} mins
                            </span>
                          )}
                        </p>
                      </div>
                      {!!nextAppointment.consultationType && (
                        <span
                          className={`${style.appointmentTypeBadge} ${
                            nextAppointment.consultationType === "VIDEO"
                              ? style.appointmentTypeBadgeVideo
                              : nextAppointment.consultationType === "PHONE"
                              ? style.appointmentTypeBadgePhone
                              : style.appointmentTypeBadgeInPerson
                          }`}
                        >
                          {formatConsultationType(
                            nextAppointment.consultationType
                          )}
                        </span>
                      )}
                    </div>

                    <div className={style.appointmentInfoGrid}>
                      {!!nextAppointment.assignedToStaffMember && (
                        <div className={style.appointmentInfoItem}>
                          <span className={style.appointmentInfoLabel}>
                            Staff Member
                          </span>
                          <span className={style.appointmentInfoValue}>
                            {`${
                              nextAppointment.assignedToStaffMember.firstName ?? ""
                            } ${
                              nextAppointment.assignedToStaffMember.lastName ?? ""
                            }`.trim()}
                          </span>
                        </div>
                      )}
                      {!!(nextAppointment.practiceService?.name || nextAppointment.serviceName) && (
                        <div className={style.appointmentInfoItem}>
                          <span className={style.appointmentInfoLabel}>
                            Service
                          </span>
                          <span className={style.appointmentInfoValue}>
                            {nextAppointment.practiceService?.name ||
                              nextAppointment.serviceName}
                          </span>
                        </div>
                      )}
                      {!!nextAppointment.companyName && (
                        <div className={style.appointmentInfoItem}>
                          <span className={style.appointmentInfoLabel}>
                            Company
                          </span>
                          <span className={style.appointmentInfoValue}>
                            {nextAppointment.companyName}
                          </span>
                        </div>
                      )}
                      {!!nextAppointment.email && (
                        <div className={style.appointmentInfoItem}>
                          <span className={style.appointmentInfoLabel}>
                            Email
                          </span>
                          <a
                            href={`mailto:${nextAppointment.email}`}
                            className={`${style.appointmentInfoValue} ${style.appointmentInfoLink}`}
                          >
                            {nextAppointment.email}
                          </a>
                        </div>
                      )}
                      <div className={style.appointmentInfoItem}>
                        <span className={style.appointmentInfoLabel}>
                          Telephone
                        </span>
                        {nextAppointment.telephoneNumber ? (
                          <a
                            href={`tel:${nextAppointment.telephoneNumber}`}
                            className={`${style.appointmentInfoValue} ${style.appointmentInfoLink}`}
                          >
                            {nextAppointment.telephoneNumber}
                          </a>
                        ) : (
                          <span className={style.appointmentInfoValue}>—</span>
                        )}
                      </div>
                      {!!bookedAt && (
                        <div className={style.appointmentInfoItem}>
                          <span className={style.appointmentInfoLabel}>
                            Booked
                          </span>
                          <span className={style.appointmentInfoValue}>
                            {bookedAt}
                          </span>
                        </div>
                      )}
                    </div>

                    {!!(nextAppointment.reasonForVisit || nextAppointment.reasonDescription) && (
                      <div className={style.appointmentReasonSection}>
                        {!!nextAppointment.reasonForVisit && (
                          <div className={style.appointmentReasonItem}>
                            <span className={style.appointmentInfoLabel}>
                              Reason for Visit
                            </span>
                            <span className={style.appointmentReasonValue}>
                              {nextAppointment.reasonForVisit}
                            </span>
                          </div>
                        )}
                        {!!nextAppointment.reasonDescription && (
                          <div className={style.appointmentReasonItem}>
                            <span className={style.appointmentInfoLabel}>
                              Reason Description
                            </span>
                            <p className={style.appointmentReasonDescription}>
                              {nextAppointment.reasonDescription}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <p className={style.appointmentDetailsEmpty}>
                    Loading appointment details…
                  </p>
                )}
              </div>
            )}

            {activeTab === "details" && showDetailsTab && (
              <div className={style.appointmentDetails}>
                <div className={style.additionalDataGrid}>
                  {Object.entries(additionalData).map(([key, value]) => (
                    <div key={key} className={style.appointmentInfoItem}>
                      <span className={style.appointmentInfoLabel}>
                        {formatKey(key)}
                      </span>
                      <span className={style.appointmentInfoValue}>
                        {typeof value === "object"
                          ? JSON.stringify(value)
                          : String(value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "services" && (
              <>
                {hasActiveConsultation && (
                  <div className={style.addServiceWrapper}>
                    <Select
                      name="service"
                      options={
                        isServicesLoading
                          ? []
                          : (services ?? []).map((item, index) => ({
                              label: item.name,
                              value: String(item.id || index),
                            }))
                      }
                      value={service}
                      onChange={(value) => setService(value)}
                      placeholder="Select Service"
                      disabled={isPaid}
                      fullWidth
                    />
                    <button
                      className={style.addServiceButton}
                      onClick={() => {
                        if (service === "Select Service") {
                          toast.error("Please select a service");
                          return;
                        }
                        addConsultationService.mutate(service);
                      }}
                      disabled={isPaid}
                    >
                      Add additional service
                    </button>
                  </div>
                )}
                <PaymentSection consultation={consultation} />
              </>
            )}

            {isVideoConsultation && (
              <div
                className={style.videoTabContent}
                style={{
                  display: activeTab === "video" ? "block" : "none",
                }}
              >
                {videoCredentials ? (
                  <VideoCallSlot className={style.videoWrapper} />
                ) : (
                  <div className={style.emptyVideoState}>
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#717186"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ marginBottom: "16px" }}
                    >
                      <path d="M23 7l-7 5 7 5V7z" />
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                    </svg>
                    <p>No active video call</p>
                    <p className={style.emptyVideoStateSubtext}>
                      Click the button below to start the video consultation
                    </p>
                    <div style={{ marginTop: "20px" }}>
                      <JoinVideoButton
                        appointment={consultation?.appointment}
                        inline={true}
                        onJoinVideo={handleJoinVideo}
                      />
                    </div>
                  </div>
                )}
                <VideoShareActions
                  patient={patient}
                  appointment={
                    convertedAppointment || consultation?.appointment
                  }
                  consultation={consultation}
                />
              </div>
            )}
          </div>
        </section>
      </div>
      <Dialog
        title="Start Consultation"
        onClose={() => setIsModalOpen({ open: false })}
        width={570}
        open={isModalOpen.open}
      >
        <StartConsultationForm
          closeModal={() => setIsModalOpen({ open: false })}
          staffName={staffName}
          hasAppointment={hasAppointment}
          appointmentId={patient?.nextAppointmentId}
        />
      </Dialog>
      <Dialog
        title="Convert to Video Consultation"
        onClose={() => setShowVideoConfirmModal(false)}
        width={400}
        open={showVideoConfirmModal}
      >
        <div style={{ padding: "16px 0" }}>
          <p style={{ marginBottom: "24px", color: "#374151" }}>
            Are you sure you want to convert this appointment to a video
            consultation?
          </p>
          <div
            style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}
          >
            <Button
              variant="secondary"
              onClick={() => setShowVideoConfirmModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateVideo}
              disabled={updateConsultationType.isPending}
            >
              {updateConsultationType.isPending ? "Converting..." : "Confirm"}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};
